import React, { Component } from "react";
import LoadingSpinner from "../components/LoadingSpinner";
import { doZonesMatch } from "../../../util/helpers";

class MatrixDwellTimeTable extends Component {
    formatTime = (milliseconds) => {
        if (!milliseconds && milliseconds !== 0) return "-";
        const minutes = Math.floor(milliseconds / 60000);
        const totalSeconds = (milliseconds % 60000) / 1000;
        const seconds = Math.floor(totalSeconds);
        const fractionalSeconds = totalSeconds - seconds;
        
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${Math.floor(fractionalSeconds * 10)}`;
    };

    getDwellCellData = (device, zone) => {
        const { triggers, dwellEvents, readTriggersDataFromLogs } = this.props;
        const deviceId = device.identifier || device._id;
        
        const entryTrigger = triggers.find(trigger => 
            trigger.deviceId === deviceId && trigger.zoneId === zone._id
        );
        
        const findDwellEvent = (dwellArray) => {
            if (!dwellArray) return null;
            let dwell = dwellArray.find(d => d.zoneId === zone._id);
            
            if (!dwell) {
                dwell = dwellArray.find(d => doZonesMatch(zone.name, d.zoneName));
            }
            
            return dwell;
        };
        
        let dwellEvent = null;
        if (readTriggersDataFromLogs) {
            dwellEvent = findDwellEvent(device.log?.dwellTriggers);
        } else {
            const filteredDwellEvents = dwellEvents?.filter(d => d.deviceId === deviceId);
            dwellEvent = findDwellEvent(filteredDwellEvents);
            
            if (!dwellEvent && entryTrigger?.dwellReceived === 1) {
                dwellEvent = entryTrigger.dwellEvent;
            }
        }
        
        let measuredDwellTime = null;
        
        if (dwellEvent && dwellEvent.dwellTime) {
            measuredDwellTime = dwellEvent.dwellTime;
        } else if (dwellEvent && dwellEvent.eventTime && entryTrigger && entryTrigger.triggerEvents && entryTrigger.triggerEvents.eventTime) {
            const dwellTime = new Date(dwellEvent.eventTime);
            const entryTime = new Date(entryTrigger.triggerEvents.eventTime);
            measuredDwellTime = dwellTime.getTime() - entryTime.getTime();
        } else if (!dwellEvent && entryTrigger && entryTrigger.exitTriggers && entryTrigger.exitTriggers.length > 0 && entryTrigger.triggerEvents && entryTrigger.triggerEvents.eventTime) {
            const exitTrigger = entryTrigger.exitTriggers[0];
            if (exitTrigger && exitTrigger.eventTime) {
                const exitTime = new Date(exitTrigger.eventTime);
                const entryTime = new Date(entryTrigger.triggerEvents.eventTime);
                measuredDwellTime = exitTime.getTime() - entryTime.getTime();
            }
        }
        
        const isDwellEnabled = zone.name && (zone.name.endsWith('-CO') || zone.name.endsWith('_CO'));
        
        let thresholdTime = null;
        if (dwellEvent && (dwellEvent.threshold || dwellEvent.threshold === 0)) {
            thresholdTime = dwellEvent.threshold;
        } else if (isDwellEnabled) {
            const zoneDwellEvents = dwellEvents?.filter(d => 
                d.zoneId === zone._id || doZonesMatch(zone.name, d.zoneName)
            );
            const dwellWithThreshold = zoneDwellEvents?.find(d => d.threshold);
            
            if (dwellWithThreshold) {
                thresholdTime = dwellWithThreshold.threshold;
            } else {
                const anyDwellWithThreshold = dwellEvents?.find(d => d.threshold);
                thresholdTime = anyDwellWithThreshold?.threshold || 60000; // Default to 60 seconds
            }
        }
        
        return { 
            dwellReceived: (dwellEvent && dwellEvent.dwellTime) ? 1 : 0, 
            measuredDwellTime, 
            hasEntry: !!entryTrigger,
            isDwellEnabled,
            thresholdTime
        };
    };

    render() {
        const { devicesShowing, zonesShowing, isLoading, toggleSelectDevice, toggleSelectZone, dwellEvents, triggers } = this.props;

        if (isLoading) {
            return <div className="matrix-table"><h4><LoadingSpinner /></h4></div>;
        }

        if (!devicesShowing.length || !zonesShowing.length) {
            return <div className="matrix-table"><h4>Select at least one zone and one device to display dwell time matrix.</h4></div>;
        }

        const somethingSelected = devicesShowing.some(d => d.isSelected) || zonesShowing.some(z => z.isSelected);
        
        const dwellRate = triggers.length > 0 ? (triggers.filter(t => t.dwellReceived === 1).length * 100 / triggers.length).toFixed(2) : 0;

        return (
            <div className="matrix-table">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <td />
                            {devicesShowing.map(device => (
                                <td
                                    key={device._id}
                                    className={`clickable ${device.isSelected || !somethingSelected ? 'bold' : 'grey'}`}
                                    onClick={() => toggleSelectDevice(device._id)}
                                >
                                    {device.name}
                                </td>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {zonesShowing.map(zone => (
                            <tr key={zone._id}>
                                <td
                                    className={`clickable ${zone.isSelected || !somethingSelected ? 'bold' : 'grey'}`}
                                    onClick={() => toggleSelectZone(zone._id)}
                                >
                                    {zone.name}
                                </td>
                                {devicesShowing.map(device => {
                                    const cellData = this.getDwellCellData(device, zone);
                                    
                                    return (
                                        <td key={device._id} className="large-text bold">
                                            {cellData.measuredDwellTime !== null ? (
                                                <span className={cellData.dwellReceived === 1 ? 'green' : 'red'}>
                                                    {this.formatTime(cellData.measuredDwellTime)}/{cellData.dwellReceived}
                                                </span>
                                            ) : cellData.isDwellEnabled ? (
                                                <span className="red">
                                                    {this.formatTime(cellData.thresholdTime || 60000)}/0
                                                </span>
                                            ) : (
                                                <span className="grey">N/A</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        <tr>
                            <td className="bold">Total:</td>
                            {devicesShowing.map(device => {
                                let deviceTriggeredCount = 0;
                                zonesShowing.forEach(zone => {
                                    const cellData = this.getDwellCellData(device, zone);
                                    if (cellData.dwellReceived === 1) {
                                        deviceTriggeredCount++;
                                    }
                                });
                                
                                return (
                                    <td key={device._id} className="bold">
                                        {deviceTriggeredCount}
                                    </td>
                                );
                            })}
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }
}

export default MatrixDwellTimeTable;